import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Highcharts from "highcharts";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import HighchartsReact from "highcharts-react-official";
import highchartsHeatMap from "highcharts/modules/heatmap";
import { property, uniq } from "lodash";
import { useMemo, useState } from "react";
import * as React from "react";

import { DesignToken } from "@livechat/design-system-react-components";

import { LoadingSpinner } from "components/loader";
import { chartCommonOptions } from "constants/chart-common-options";
import { heatmapCommonOptions } from "constants/heatmap-common-options";
import { trackDatapointClicked, trackSaveAsCSV } from "helpers/analytics-reports";
import { deepMerge } from "helpers/object";
import { IChartSummary } from "store/views/reports";
import { withVar } from "themes/helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { getExportDataByChartOptions, mapChartLegendColors, mapChartSeriesColors } from "../helpers";

import { ChartBreakdown } from "./ChartBreakdown";
import { ChartHeader } from "./ChartHeader";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { ChartLegend } from "./ChartLegend";

highchartsHeatMap(Highcharts);

const StyledContainer = styled.div`
  position: relative;
`;

const StyledEmptyDataContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
`;

const StyledLoadingLabel = styled.div`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
  font-size: 13px;
  line-height: 22px;
  margin-left: 3px;
`;

const EmptyStateWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
`;

const chartContainerStyles = css`
  position: relative;

  &:hover {
    z-index: 2;
  }

  & > * {
    overflow: initial !important;
  }

  .highcharts-container {
    overflow: visible !important;
  }
`;

export interface IChartProps {
  title: string;
  description?: string;
  hideHeader?: boolean;
  loading: boolean;
  options?: Highcharts.Options;
  emptyState?: React.ReactElement;
  tooltip?: React.ReactNode;
  legend?: IChartLegend[];
  exportFileName?: string;
  ignoreAloneSeries?: boolean;
  breakdownChartOptions?: Highcharts.Options;
  disableBreakdown?: boolean;
  disabledBreakdownTotal?: boolean;
  breakdownValueParser?(value: number | string): number | string;
  breakdownCustomTitle?: React.ReactNode;
  legendUrlResolver?(legendSeries: IChartLegend): string;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  exportMapper?(series: any): Highcharts.Series;
  customComponent?: React.ReactNode;
}

export type IChartLegend = IChartSummary;

export const Chart: React.FC<IChartProps> = ({
  title,
  hideHeader,
  loading,
  options,
  emptyState,
  tooltip,
  legend = [],
  ignoreAloneSeries = true,
  breakdownChartOptions,
  disableBreakdown,
  disabledBreakdownTotal,
  breakdownValueParser,
  breakdownCustomTitle,
  legendUrlResolver,
  exportFileName,
  exportMapper,
  customComponent,
}) => {
  const [chartOptions, setChartOptions] = useState(options);
  const [activeSeries, setActiveSeries] = useState(null);

  if (!loading && chartOptions !== options) {
    setChartOptions(options);
  }

  const commonOptions =
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    chartOptions && chartOptions.chart.type === "heatmap" ? heatmapCommonOptions : chartCommonOptions;

  const seriesOrder = uniq(
    // @ts-expect-error TS(2339)
    (chartOptions && chartOptions.series ? chartOptions.series : []).map(series => series.color).filter(Boolean)
  );

  const memoizedOptions = useMemo(() => {
    const series = (chartOptions && chartOptions.series ? chartOptions.series : []).sort(
      // @ts-expect-error TS(2339)
      (a, b) => seriesOrder.indexOf(a.color) - seriesOrder.indexOf(b.color)
    );

    const mergedOptions = deepMerge(
      true,
      {},
      commonOptions,
      chartOptions && {
        ...chartOptions,
        // @ts-expect-error TS(2345)
        series: mapChartSeriesColors(series, activeSeries),
      }
    );

    const navigationResolver = property<unknown, () => void>(["plotOptions", "series", "point", "events", "click"])(
      options
    );

    return deepMerge(true, {}, mergedOptions, {
      plotOptions: {
        series: {
          // @ts-expect-error TS(2774): This condition will always return true since this ... Remove this comment to see the full error message
          cursor: navigationResolver ? "pointer" : "auto",
          point: {
            events: {
              click: navigationResolver
                ? // FIXME: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
                  function pointClick(this: any) {
                    trackDatapointClicked(title);
                    navigationResolver.call(this);
                  }
                : null,
            },
          },
        },
      },
    });
  }, [chartOptions, commonOptions, activeSeries, options, seriesOrder, title]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDataExported = () => trackSaveAsCSV(title);

  const memoizedExportData = useMemo(
    () => (exportFileName && getExportDataByChartOptions(memoizedOptions, exportMapper)) || undefined,
    [memoizedOptions, exportFileName, exportMapper]
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  const handleChatLegendSeriesMouseOver = (legendSeries: any) => {
    setActiveSeries(legendSeries.color || null);
  };
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleChatLegendSeriesMouseLeave = () => setActiveSeries(null);

  const [chart, setChart] = useState(null);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  const handleChartCreated = (chartObj: any) => setChart(chartObj);

  const memoizedLegend = useMemo(() => {
    const sortedLegend = legend.sort((a, b) => seriesOrder.indexOf(a.color) - seriesOrder.indexOf(b.color));
    const colorizedLegend = mapChartLegendColors(sortedLegend);

    if (ignoreAloneSeries && colorizedLegend.length === 1) {
      return colorizedLegend.map(legendSeries => ({
        ...legendSeries,
        color: null,
      }));
    }

    return colorizedLegend;
  }, [legend, seriesOrder, ignoreAloneSeries]);

  // HACK: Highcharts incorrectly takes gradient reference using url of site that chart was created.
  // If address is changed but the chars is still active, then gradients lose reference
  // I have updated the renderer url using highcharts api to avoid this situation
  // @ts-expect-error TS(2339): Property 'renderer' does not exist on type 'never'... Remove this comment to see the full error message
  if (chart && chart.renderer.url !== window.location.href) {
    // @ts-expect-error TS(2339): Property 'renderer' does not exist on type 'never'... Remove this comment to see the full error message
    chart.renderer.url = window.location.href;
    // @ts-expect-error TS(2339): Property 'render' does not exist on type 'never'.
    chart.render();
  }

  if (activeSeries && !(memoizedLegend as IChartLegend[]).find(legendSeries => legendSeries.color === activeSeries)) {
    setActiveSeries(null);
  }

  return (
    <div>
      <StyledContainer>
        {!hideHeader && (
          <ChartHeader
            title={title}
            loading={!!options && loading}
            tooltip={tooltip}
            exportFileName={exportFileName}
            exportData={memoizedExportData}
            onDataExported={handleDataExported}
          />
        )}
        {!options && loading && (
          <StyledEmptyDataContainer>
            <LoadingSpinner isVisible size="20px" />
            <StyledLoadingLabel>Loading...</StyledLoadingLabel>
          </StyledEmptyDataContainer>
        )}
        {legend && (
          <ChartLegend
            // @ts-expect-error TS(2322)
            legend={memoizedLegend}
            activeIndex={activeSeries}
            onMouseOver={handleChatLegendSeriesMouseOver}
            onMouseLeave={handleChatLegendSeriesMouseLeave}
            urlResolver={legendUrlResolver}
            trackingChartName={title}
            ignoreAloneSeries={ignoreAloneSeries}
          />
        )}
        {options && (
          <div css={chartContainerStyles}>
            <HighchartsReact highcharts={Highcharts} options={memoizedOptions} callback={handleChartCreated} />
          </div>
        )}
        {emptyState && <EmptyStateWrapper>{emptyState}</EmptyStateWrapper>}
        {customComponent}
        {!disableBreakdown && (
          <ChartBreakdown
            chartTitle={title}
            customTitle={breakdownCustomTitle}
            chartOptions={breakdownChartOptions || memoizedOptions}
            loading={options && loading}
            exportFileName={exportFileName}
            exportData={memoizedExportData}
            disabledTotal={disabledBreakdownTotal}
            valueParser={breakdownValueParser}
            onDataExported={handleDataExported}
          />
        )}
      </StyledContainer>
    </div>
  );
};
